import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Trash2, Circle, Square, Sparkles,
  StepBack, StepForward, Loader2, MousePointerClick, Eye, EyeOff,
  ZoomIn, ZoomOut, Maximize, Copy, ClipboardPaste, Lock,
  CheckCheck, Wand2, Crosshair,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIB = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233, 377, 610, 987];
const VZOOMS = [1, 2, 4, 8, 16];
const N_THUMBS = 22;

/* interpolacion lineal entre fotogramas clave (igual que el backend) */
const interp = (kfs, f) => {
  if (!kfs || kfs.length === 0 || f < kfs[0].f || f > kfs[kfs.length - 1].f) return null;
  for (let i = 0; i < kfs.length - 1; i++) {
    const a = kfs[i], b = kfs[i + 1];
    if (a.f <= f && f <= b.f) {
      const t = (f - a.f) / Math.max(1, b.f - a.f);
      return { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y), w: a.w + t * (b.w - a.w), h: a.h + t * (b.h - a.h) };
    }
  }
  const k = kfs[kfs.length - 1];
  return { x: k.x, y: k.y, w: k.w, h: k.h };
};

const fmtS = (frames, fps) => {
  const s = frames / fps;
  if (s < 60) return `${s.toFixed(2).replace(".", ",")} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
};

const fmtClock = (sec) => {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = Math.floor(sec % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
};

const confInfo = (a) => {
  if (a.locked) return { txt: "Bloqueado", cls: "bg-[#F0EBE4] text-[#57503F] border-[#DDD2C2]" };
  if (a.reviewed) return { txt: "Verificado", cls: "bg-[#EAF7EE] text-[#1E6B3A] border-[#BFE5CB]" };
  if (a.source === "manual") return { txt: "Manual", cls: "bg-[#E8F0FE] text-[#1A56DB] border-[#C3D4F5]" };
  if ((a.conf || 0) >= 0.55) return { txt: "IA segura", cls: "bg-[#EAF7EE] text-[#1E6B3A] border-[#BFE5CB]" };
  return { txt: "Revisar", cls: "bg-[#FEF6E7] text-[#92600A] border-[#F5DFAE]" };
};

const borderColor = (a, selected) => {
  if (selected) return "#F26F21";
  if (a.locked) return "#8A7F73";
  if (a.source === "manual") return "#1A56DB";
  return (a.conf || 0) >= 0.55 ? "#2FA45A" : "#E8A213";
};

const Chip = ({ active, suggested, onClick, children, testid, title }) => (
  <button
    data-testid={testid}
    title={title}
    onClick={onClick}
    className={`shrink-0 h-7 min-w-[34px] px-1.5 rounded-lg text-[11px] font-bold tabular-nums transition-all ${
      active
        ? "bg-[#F26F21] text-white"
        : suggested
        ? "bg-[#FFF3E9] border-2 border-[#F26F21] text-[#C2570F]"
        : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
    }`}
  >
    {children}
  </button>
);

const ReviewEditor = ({ videoId, style, setStyle, strength, setStrength, onRenderStarted, cost, credits, mode, frameRange }) => {
  const [meta, setMeta] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [frame, setFrame] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [drag, setDrag] = useState(null);
  const [tempRect, setTempRect] = useState(null);

  /* navegacion fibonacci */
  const [jump, setJump] = useState(21);

  /* propagacion inteligente */
  const [propMode, setPropMode] = useState("end"); // here | fib | track | end
  const [propN, setPropN] = useState(55);
  const [propDir, setPropDir] = useState("fwd"); // fwd | back

  /* zoom visual */
  const [vZoom, setVZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [stageW, setStageW] = useState(0);

  /* zoom temporal timeline */
  const [tlLevel, setTlLevel] = useState(0);
  const [tlStart, setTlStart] = useState(0); // segundos

  /* portapapeles + edicion masiva */
  const [clipboard, setClipboard] = useState(null);
  const [checked, setChecked] = useState(new Set());

  const outerRef = useRef(null);
  const tlRef = useRef(null);
  const zoomAtRef = useRef(() => {});
  const tlZoomRef = useRef(() => {});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/videos/${videoId}/annotations`);
        setMeta(data);
        setAnnotations(data.annotations || []);
        setFrame(frameRange?.start || 1);
      } catch {
        toast.error("No se pudo cargar el análisis.");
      }
    })();
  }, [videoId]);

  /* autoguardado de anotaciones (debounce 2s) */
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!meta) return;
    if (!loadedRef.current) { loadedRef.current = true; return; }
    const t = setTimeout(() => {
      axios.put(`${API}/videos/${videoId}/annotations`, { annotations }).catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
  }, [annotations, meta, videoId]);

  const fps = meta?.fps || 25;
  const totalFrames = meta?.total_frames || 1;
  /* limites del tramo activo (checkpoints): el editor trabaja SOLO en ese rango */
  const loF = frameRange?.start || 1;
  const hiF = frameRange ? Math.min(frameRange.end || totalFrames, totalFrames) : totalFrames;
  const loSec = (loF - 1) / fps;
  const totalSecs = Math.max(1, Math.floor((hiF - loF + 1) / fps));

  /* al cambiar de tramo: saltar a su inicio y reencuadrar la timeline */
  useEffect(() => {
    setFrame(frameRange?.start || 1);
    setSelectedId(null);
    setTlLevel(0);
    setTlStart(0);
    setChecked(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameRange?.start, frameRange?.end]);

  /* elementos que pertenecen al tramo activo (compacto: menos fatiga ocular) */
  const rangeAnns = useMemo(() => {
    if (!frameRange) return annotations;
    return annotations.filter((a) => {
      const kfs = a.keyframes || [];
      if (!kfs.length) return true;
      let mn = Infinity, mx = -Infinity;
      for (const k of kfs) { if (k.f < mn) mn = k.f; if (k.f > mx) mx = k.f; }
      return !(mx < loF || mn > hiF);
    });
  }, [annotations, frameRange, loF, hiF]);

  /* niveles de zoom temporal segun duracion */
  const TL_LEVELS = useMemo(() => {
    const lv = [{ id: "full", label: "Todo", span: totalSecs }];
    [[600, "10 min"], [300, "5 min"], [60, "1 min"], [10, "10 s"], [1, "1 s"]].forEach(([s, l]) => {
      if (totalSecs > s) lv.push({ id: s, label: l, span: s });
    });
    lv.push({ id: "frame", label: "Frames", span: N_THUMBS / fps });
    return lv;
  }, [totalSecs, fps]);

  const curLevel = TL_LEVELS[Math.min(tlLevel, TL_LEVELS.length - 1)];
  const curSpan = curLevel.id === "full" ? totalSecs : curLevel.span;

  /* ---------- medidas del escenario ---------- */
  useEffect(() => {
    const m = () => outerRef.current && setStageW(outerRef.current.clientWidth);
    m();
    window.addEventListener("resize", m);
    return () => window.removeEventListener("resize", m);
  }, [meta]);

  const baseH = meta && stageW ? (stageW * meta.height) / meta.width : 0;
  const imgScale = meta && stageW ? (stageW * vZoom) / meta.width : 1;

  const clampPan = useCallback(
    (p, z) => {
      const innerW = stageW * z, innerH = baseH * z;
      return {
        x: Math.min(0, Math.max(stageW - innerW, p.x)),
        y: Math.min(0, Math.max(baseH - innerH, p.y)),
      };
    },
    [stageW, baseH]
  );

  /* ---------- zoom visual (tipo Photoshop) ---------- */
  const zoomAt = useCallback(
    (cx, cy, dirIn) => {
      const idx = VZOOMS.indexOf(vZoom);
      const ni = dirIn ? Math.min(VZOOMS.length - 1, idx + 1) : Math.max(0, idx - 1);
      const nz = VZOOMS[ni];
      if (nz === vZoom) return;
      const px = (cx - pan.x) / vZoom, py = (cy - pan.y) / vZoom;
      const np = clampPan({ x: cx - px * nz, y: cy - py * nz }, nz);
      setVZoom(nz);
      setPan(nz === 1 ? { x: 0, y: 0 } : np);
    },
    [vZoom, pan, clampPan]
  );
  zoomAtRef.current = zoomAt;

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAtRef.current(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [meta]);

  /* ---------- zoom temporal (Ctrl + rueda sobre la timeline) ---------- */
  const setLevel = useCallback(
    (idx) => {
      const i = Math.max(0, Math.min(TL_LEVELS.length - 1, idx));
      const span = TL_LEVELS[i].id === "full" ? totalSecs : TL_LEVELS[i].span;
      const cur = (frame - loF) / fps;
      setTlLevel(i);
      setTlStart(Math.max(0, Math.min(Math.max(0, totalSecs - span), cur - span / 2)));
    },
    [TL_LEVELS, totalSecs, frame, fps]
  );
  tlZoomRef.current = (dirIn) => setLevel(tlLevel + (dirIn ? 1 : -1));

  useEffect(() => {
    const el = tlRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      tlZoomRef.current(e.deltaY < 0);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [meta]);

  /* recentrar ventana si el fotograma sale de ella */
  useEffect(() => {
    const cur = (frame - loF) / fps;
    if (cur < tlStart || cur > tlStart + curSpan) {
      setTlStart(Math.max(0, Math.min(Math.max(0, totalSecs - curSpan), cur - curSpan / 2)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame]);

  /* ---------- miniaturas de la timeline ---------- */
  const stripItems = useMemo(() => {
    const out = [];
    if (curLevel.id === "frame") {
      const startF = Math.max(loF, Math.round((loSec + tlStart) * fps) + 1);
      for (let i = 0; i < N_THUMBS; i++) {
        const f = startF + i;
        if (f > hiF) break;
        out.push({ key: `f${f}`, f, label: `#${f}` });
      }
    } else {
      const step = Math.max(1 / fps, curSpan / N_THUMBS);
      for (let i = 0; i < N_THUMBS; i++) {
        const sec = tlStart + i * step;
        if (sec > totalSecs) break;
        const f = Math.min(hiF, Math.max(loF, Math.round((loSec + sec) * fps) + 1));
        out.push({ key: `s${i}-${f}`, f, label: fmtClock(loSec + sec) });
      }
    }
    return out;
  }, [curLevel, curSpan, tlStart, totalSecs, hiF, loF, loSec, fps]);

  const go = useCallback(
    (d) => setFrame((f) => Math.min(hiF, Math.max(loF, f + d))),
    [hiF, loF]
  );

  /* ---------- sugerencia fibonacci adaptativa ---------- */
  const suggestion = useMemo(() => {
    const a = annotations.find((x) => x.id === selectedId);
    if (!a || !meta) return null;
    const kfs = [...a.keyframes].sort((p, q) => p.f - q.f);
    let speed = 0, cnt = 0;
    for (let i = 0; i < kfs.length - 1; i++) {
      const k1 = kfs[i], k2 = kfs[i + 1];
      if (k2.f < frame - 89 || k1.f > frame + 89) continue;
      const df = Math.max(1, k2.f - k1.f);
      const d = Math.hypot((k2.x + k2.w / 2) - (k1.x + k1.w / 2), (k2.y + k2.h / 2) - (k1.y + k1.h / 2));
      speed += d / df;
      cnt++;
    }
    if (!cnt) return { n: 89, tag: "sin historial" };
    const v = speed / cnt / meta.width;
    if (v < 0.0006) return { n: 21, tag: "objeto estático" };
    if (v < 0.002) return { n: 55, tag: "objeto lento" };
    if (v < 0.005) return { n: 144, tag: "circulación urbana" };
    if (v < 0.011) return { n: 377, tag: "circulación rápida" };
    return { n: 610, tag: "autovía" };
  }, [selectedId, annotations, frame, meta]);

  /* ---------- edicion con propagacion ---------- */
  const applyEdit = useCallback(
    (annId, newRect) => {
      setAnnotations((prev) =>
        prev.map((a) => {
          if (a.id !== annId || a.locked) return a;
          const kfs = [...a.keyframes].sort((p, q) => p.f - q.f);
          const old = interp(kfs, frame) || newRect;
          const dx = newRect.x - old.x, dy = newRect.y - old.y;
          const dw = newRect.w - old.w, dh = newRect.h - old.h;
          let lo = frame, hi = frame;
          if (propMode === "end") hi = Infinity;
          else if (propMode === "track") { lo = -Infinity; hi = Infinity; }
          else if (propMode === "fib") {
            if (propDir === "fwd") hi = frame + propN;
            else lo = frame - propN;
          }
          const out = kfs
            .filter((k) => k.f !== frame)
            .map((k) =>
              k.f >= lo && k.f <= hi
                ? { ...k, x: k.x + dx, y: k.y + dy, w: Math.max(8, k.w + dw), h: Math.max(8, k.h + dh) }
                : k
            );
          // AUTOMÁTICO: fijar la trayectoria antigua justo fuera del rango editado
          // (equivale a "aparece aquí"/"desaparece aquí" sin pasos manuales):
          // mover el recuadro en f10 no arrastra hacia atrás la posición de f9.
          const pin = (atF) => {
            if (atF < kfs[0].f || atF > kfs[kfs.length - 1].f) return;
            if (out.some((k) => k.f === atF)) return;
            const r = interp(kfs, atF);
            if (r) out.push({ f: atF, x: r.x, y: r.y, w: r.w, h: r.h });
          };
          if (lo !== -Infinity) pin(lo - 1);
          if (hi !== Infinity) pin(hi + 1);
          out.push({ f: frame, x: newRect.x, y: newRect.y, w: newRect.w, h: newRect.h });
          out.sort((p, q) => p.f - q.f);
          return { ...a, keyframes: out, source: a.source === "ia" ? "ia-editada" : a.source };
        })
      );
    },
    [frame, propMode, propN, propDir]
  );

  /* ---------- arrastre: anotaciones + pan del lienzo ---------- */
  const onStagePointerMove = (e) => {
    if (!drag) return;
    if (drag.mode === "pan") {
      setPan(clampPan({ x: drag.origPan.x + (e.clientX - drag.startX), y: drag.origPan.y + (e.clientY - drag.startY) }, vZoom));
      return;
    }
    const dx = (e.clientX - drag.startX) / imgScale;
    const dy = (e.clientY - drag.startY) / imgScale;
    const o = drag.orig;
    if (drag.mode === "move") setTempRect({ x: o.x + dx, y: o.y + dy, w: o.w, h: o.h });
    else setTempRect({ x: o.x, y: o.y, w: Math.max(10, o.w + dx), h: Math.max(10, o.h + dy) });
  };
  const onStagePointerUp = () => {
    if (drag && drag.mode !== "pan" && tempRect) applyEdit(drag.annId, tempRect);
    setDrag(null);
    setTempRect(null);
  };

  const startDrag = (e, ann, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(ann.id);
    if (ann.locked) return;
    const rect = interp(ann.keyframes, frame);
    if (!rect) return;
    setDrag({ annId: ann.id, mode, startX: e.clientX, startY: e.clientY, orig: rect });
    setTempRect(rect);
  };

  const onStageBgDown = (e) => {
    if (vZoom > 1) {
      e.preventDefault();
      setDrag({ mode: "pan", startX: e.clientX, startY: e.clientY, origPan: pan });
    } else {
      setSelectedId(null);
    }
  };

  /* ---------- alta manual / copiar / pegar ---------- */
  const spanFrames = useCallback(() => {
    if (propMode === "here") return Math.max(2, Math.round(fps));
    if (propMode === "fib") return propN;
    return hiF - frame;
  }, [propMode, propN, hiF, frame, fps]);

  const createAnn = useCallback(
    (shape, rect, extraStyle) => {
      const endF = Math.min(hiF, frame + Math.max(2, spanFrames()));
      const ann = {
        id: `m${Date.now()}${Math.floor(Math.random() * 999)}`,
        shape, label: "manual", conf: 1, color: null, source: "manual",
        ...(extraStyle ? { style: extraStyle } : {}),
        keyframes: [{ f: frame, ...rect }, { f: endF, ...rect }],
      };
      setAnnotations((prev) => [...prev, ann]);
      setSelectedId(ann.id);
      return ann;
    },
    [frame, hiF, spanFrames]
  );

  const addManual = (shape) => {
    if (!meta) return;
    const w = meta.width * 0.16;
    const h = shape === "oval" ? meta.width * 0.2 : meta.width * 0.06;
    createAnn(shape, { x: meta.width / 2 - w / 2, y: meta.height / 2 - h / 2, w, h });
    toast.success(shape === "oval" ? "Óvalo añadido: arrástralo a su sitio" : "Recuadro añadido: arrástralo a su sitio");
  };

  const copySel = useCallback(() => {
    const a = annotations.find((x) => x.id === selectedId);
    if (!a) { toast.error("Selecciona un elemento para copiar."); return; }
    const rect = interp([...a.keyframes].sort((p, q) => p.f - q.f), frame);
    if (!rect) { toast.error("El elemento no es visible en este fotograma."); return; }
    setClipboard({ shape: a.shape, style: a.style || null, rect });
    toast.success("Difuminado copiado (Ctrl+V para pegar)");
  }, [annotations, selectedId, frame]);

  const paste = useCallback(() => {
    if (!clipboard || !meta) return;
    const off = meta.width * 0.02;
    const rect = {
      x: Math.min(meta.width - clipboard.rect.w, clipboard.rect.x + off),
      y: Math.min(meta.height - clipboard.rect.h, clipboard.rect.y + off),
      w: clipboard.rect.w, h: clipboard.rect.h,
    };
    createAnn(clipboard.shape, rect, clipboard.style);
    toast.success("Difuminado pegado en este fotograma");
  }, [clipboard, meta, createAnn]);

  /* ---------- acciones del elemento seleccionado ---------- */
  const duplicateSel = useCallback(() => {
    const a = annotations.find((x) => x.id === selectedId);
    if (!a || !meta) { toast.error("Selecciona un elemento para duplicar."); return; }
    const off = meta.width * 0.02;
    const copy = {
      ...a,
      id: `m${Date.now()}${Math.floor(Math.random() * 999)}`,
      source: "manual",
      label: a.label.includes("(copia)") ? a.label : `${a.label} (copia)`,
      locked: false,
      reviewed: false,
      keyframes: a.keyframes.map((k) => ({ ...k, x: k.x + off, y: k.y + off })),
    };
    setAnnotations((prev) => [...prev, copy]);
    setSelectedId(copy.id);
    toast.success("Elemento duplicado con todo su recorrido");
  }, [annotations, selectedId, meta]);

  const setSelProp = (patch, msg) => {
    setAnnotations((prev) => prev.map((a) => (a.id === selectedId ? { ...a, ...patch } : a)));
    if (msg) toast.success(msg);
  };

  /* ---------- Seguir hasta desaparecer (tracking visual, sin OCR) ---------- */
  const [trackBusy, setTrackBusy] = useState(false);
  const [trackInfo, setTrackInfo] = useState("");
  const [trackMode, setTrackMode] = useState("region"); // region | plate | vehicle
  const [trackFit, setTrackFit] = useState("pos_size"); // pos | pos_size
  const cancelTrackRef = useRef(false);

  /* ---------- Dakota: enseñar apariencia y crear Track ID persistente ---------- */
  const teachDakota = useCallback(() => {
    const a = annotations.find((x) => x.id === selectedId);
    if (!a || !meta) return;
    const kfs = [...a.keyframes].sort((p, q) => p.f - q.f);
    const rect = interp(kfs, frame);
    if (!rect) { toast.error("El elemento no es visible en este fotograma."); return; }
    const nextTrackId = 1 + annotations.reduce((mx, x) => {
      const mt = /^Track #(\d+)/.exec(x.label || "");
      return mt ? Math.max(mx, parseInt(mt[1], 10)) : mx;
    }, 0);
    const label = /^Track #/.test(a.label || "") ? a.label : `Track #${nextTrackId}`;
    setAnnotations((prev) => prev.map((x) => (x.id === selectedId
      ? { ...x, label, dakota: { f: frame, rect }, source: x.source === "ia" ? "ia-editada" : x.source }
      : x)));
    toast.success(`Dakota ha aprendido la apariencia de este objeto (${label}). Ahora pulsa «Seguir hasta desaparecer».`);
  }, [annotations, selectedId, meta, frame]);

  const followUntilGone = useCallback(async () => {
    const a = annotations.find((x) => x.id === selectedId);
    if (!a || !meta) return;
    const kfs = [...a.keyframes].sort((p, q) => p.f - q.f);
    const rect = interp(kfs, frame);
    if (!rect) { toast.error("El elemento no es visible en este fotograma."); return; }
    cancelTrackRef.current = false;
    setTrackBusy(true);
    const annId = a.id;
    const base = kfs.filter((k) => k.f < frame); // recalcular desde este punto
    let curF = frame;
    let curRect = { x: rect.x, y: rect.y, w: rect.w, h: rect.h };
    let acc = [{ f: frame, ...curRect }];
    let reason = "max";
    let lastF = frame;
    try {
      for (let i = 0; i < 25 && !cancelTrackRef.current; i++) {
        setTrackInfo(`Siguiendo… fotograma ${curF} · ${(curF / fps).toFixed(1)}s`);
        const { data } = await axios.post(`${API}/videos/${videoId}/track`, {
          frame: curF, rect: curRect, mode: trackMode, fit: trackFit,
          max_frames: Math.max(10, Math.min(300, hiF - curF + 1)),
        });
        if (data.keyframes?.length) {
          acc = acc.concat(data.keyframes);
          setAnnotations((prev) =>
            prev.map((x) => (x.id === annId
              ? { ...x, keyframes: [...base, ...acc], source: x.source === "ia" ? "ia-editada" : x.source }
              : x))
          );
          const last = data.keyframes[data.keyframes.length - 1];
          curRect = { x: last.x, y: last.y, w: last.w, h: last.h };
        }
        reason = data.end_reason;
        lastF = data.last_frame || curF;
        if (lastF >= hiF) { reason = reason === "max" ? "end_of_video" : reason; break; }
        if (reason !== "max") break;
        curF = lastF;
      }
      const msgs = {
        lost: "el objeto desapareció de la escena (salida u oclusión >2s)",
        end_of_video: "fin del vídeo",
        max: cancelTrackRef.current ? "detenido por ti" : "límite de seguimiento alcanzado",
      };
      const tracked = acc.length ? acc[acc.length - 1].f - frame : 0;
      // IDENTIDAD PERSISTENTE (Dakota): un objeto = un track. Absorber detecciones
      // duplicadas que solapen con la trayectoria seguida. Calculado FUERA del updater.
      const lastAccF = acc.length ? acc[acc.length - 1].f : frame;
      // PRIVACIDAD: si Dakota pierde el objeto antes de que acabe la detección
      // original, se conserva la cobertura original posterior (nunca destapar).
      const tail = reason === "lost" ? kfs.filter((k) => k.f > lastAccF) : [];
      const trackedKfs = [...base, ...acc, ...tail].sort((p, q) => p.f - q.f);
      const overlapCoef = (ra, rb) => {
        const ix = Math.max(0, Math.min(ra.x + ra.w, rb.x + rb.w) - Math.max(ra.x, rb.x));
        const iy = Math.max(0, Math.min(ra.y + ra.h, rb.y + rb.h) - Math.max(ra.y, rb.y));
        return (ix * iy) / Math.max(1, Math.min(ra.w * ra.h, rb.w * rb.h));
      };
      const dupIds = annotations
        .filter((x) => {
          if (x.id === annId || x.locked || x.source === "manual") return false;
          const ks = [...x.keyframes].sort((p, q) => p.f - q.f);
          if (!ks.length) return false;
          // solo el rango realmente SEGUIDO (sin la cola conservada): evita absorber
          // detecciones de otros vehículos posteriores a la pérdida del objeto
          // un duplicado del MISMO objeto ya existe cuando arranca el track;
          // los objetos que aparecen mucho después son vehículos distintos
          if (ks[0].f > frame + Math.round(fps)) return false;
          const f0 = Math.max(ks[0].f, frame);
          const f1 = Math.min(ks[ks.length - 1].f, lastAccF);
          if (f1 - f0 < 2) return false;
          const ra0 = interp(ks, f0), rb0 = interp(trackedKfs, f0);
          if (!ra0 || !rb0 || overlapCoef(ra0, rb0) <= 0.5) return false;
          let hit = 0, tot = 0;
          for (let s = 0; s < 7; s++) {
            const fq = Math.round(f0 + (s * (f1 - f0)) / 6);
            const ra = interp(ks, fq), rb = interp(trackedKfs, fq);
            if (!ra || !rb) continue;
            tot++;
            if (overlapCoef(ra, rb) > 0.5) hit++;
          }
          return tot >= 3 && hit / tot >= 0.6;
        })
        .map((x) => x.id);
      const curAnn = annotations.find((x) => x.id === annId);
      const nextTrackId = 1 + annotations.reduce((mx, x) => {
        const mt = /^Track #(\d+)/.exec(x.label || "");
        return mt ? Math.max(mx, parseInt(mt[1], 10)) : mx;
      }, 0);
      const newLabel = /^Track #/.test(curAnn?.label || "") ? curAnn.label : `Track #${nextTrackId}`;
      const finalAnns = annotations
        .filter((x) => !dupIds.includes(x.id))
        .map((x) => (x.id === annId ? { ...x, keyframes: trackedKfs, label: newLabel } : x));
      setAnnotations(finalAnns);
      // persistencia inmediata: el resultado del track y las absorciones no
      // dependen del debounce del autoguardado
      axios.put(`${API}/videos/${videoId}/annotations`, { annotations: finalAnns }).catch(() => {});
      toast.success(
        `${newLabel} · Dakota siguió ${tracked} fotogramas (${(tracked / fps).toFixed(1)}s) · ${msgs[reason] || reason}` +
        (dupIds.length ? ` · ${dupIds.length} detecciones duplicadas absorbidas` : "")
      );
      setFrame(Math.min(hiF, Math.max(loF, lastF)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo completar el seguimiento.");
    } finally {
      setTrackBusy(false);
      setTrackInfo("");
    }
  }, [annotations, selectedId, meta, frame, fps, hiF, loF, videoId, trackMode, trackFit]);

  /* ---------- atajos de teclado ---------- */
  useEffect(() => {
    const h = (e) => {
      if (/input|textarea|select/i.test(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "c") { e.preventDefault(); copySel(); }
      else if ((e.ctrlKey || e.metaKey) && k === "v") { e.preventDefault(); paste(); }
      else if ((e.ctrlKey || e.metaKey) && k === "d") { e.preventDefault(); duplicateSel(); }
      else if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        setAnnotations((prev) => prev.filter((a) => a.id !== selectedId));
        setSelectedId(null);
        toast.success("Elemento eliminado");
      }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(e.shiftKey ? jump : 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(e.shiftKey ? -jump : -1); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [copySel, paste, duplicateSel, selectedId, go, jump]);

  /* ---------- recortar vida del elemento ---------- */
  const trimAnn = (annId, side) => {
    let result = null; // 'ok' | 'invisible' | 'vacio'
    setAnnotations((prev) =>
      prev.map((a) => {
        if (a.id !== annId) return a;
        const kfs = [...a.keyframes].sort((p, q) => p.f - q.f);
        const rect = interp(kfs, frame);
        if (!rect) { result = "invisible"; return a; }
        const out =
          side === "end"
            ? [...kfs.filter((k) => k.f < frame), { f: frame, ...rect }]
            : [{ f: frame, ...rect }, ...kfs.filter((k) => k.f > frame)];
        if (out.length < 2 || out[out.length - 1].f - out[0].f < 1) {
          result = "vacio";
          return a;
        }
        result = "ok";
        return { ...a, keyframes: out, source: a.source === "ia" ? "ia-editada" : a.source };
      })
    );
    if (result === "ok") {
      toast.success(side === "end"
        ? `El elemento desaparecerá a partir del fotograma ${frame} (${(frame / fps).toFixed(1)}s)`
        : `El elemento aparecerá desde el fotograma ${frame} (${(frame / fps).toFixed(1)}s)`);
    } else if (result === "vacio") {
      toast.error("Ese recorte dejaría el elemento sin duración. Muévete a un fotograma intermedio.");
    } else {
      toast.error("El elemento no está visible en este fotograma.");
    }
  };

  const removeAnn = (id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    if (selectedId === id) setSelectedId(null);
    setChecked((prev) => { const n = new Set(prev); n.delete(id); return n; });
  };

  /* ---------- edicion masiva ---------- */
  const toggleCheck = (id) =>
    setChecked((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleAll = () =>
    setChecked((prev) => (prev.size === rangeAnns.length ? new Set() : new Set(rangeAnns.map((a) => a.id))));
  const bulkSet = (fn, msg) => {
    setAnnotations((prev) => prev.map((a) => (checked.has(a.id) ? fn(a) : a)));
    if (msg) toast.success(msg);
  };
  const bulkDelete = () => {
    setAnnotations((prev) => prev.filter((a) => !checked.has(a.id)));
    toast.success(`${checked.size} elementos eliminados`);
    setChecked(new Set());
  };

  /* ---------- guardar + renderizar ---------- */
  const startRender = async () => {
    try {
      setRendering(true);
      await axios.put(`${API}/videos/${videoId}/annotations`, { annotations });
      await axios.post(`${API}/videos/${videoId}/render`, { style: `${style}|${strength || 3}` });
      onRenderStarted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el renderizado.");
      setRendering(false);
    }
  };

  if (!meta) {
    return (
      <div className="rounded-3xl bg-[#FFF7F0] border border-[#F5E7D8] p-16 flex items-center justify-center gap-3">
        <Loader2 size={22} className="text-[#F26F21] animate-spin" />
        <p className="text-[14px] font-bold text-[#57503F]">Cargando análisis…</p>
      </div>
    );
  }

  const visibleAnns = annotations
    .map((a) => ({ ann: a, rect: a.id === drag?.annId && tempRect ? tempRect : interp(a.keyframes, frame) }))
    .filter((v) => v.rect);
  const selAnn = annotations.find((a) => a.id === selectedId);
  const selVisible = selAnn ? interp([...selAnn.keyframes].sort((p, q) => p.f - q.f), frame) !== null : false;
  const dudosas = rangeAnns.filter((a) => !a.reviewed && a.source !== "manual" && (a.conf || 0) < 0.55).length;

  /* revisión guiada: confirma el caso actual y salta al siguiente dudoso */
  const nextDoubt = () => {
    const list = rangeAnns
      .filter((a) => !a.reviewed && a.source !== "manual" && (a.conf || 0) < 0.55 && a.keyframes?.length)
      .sort((x, y) => (x.keyframes[0].f || 0) - (y.keyframes[0].f || 0));
    if (!list.length) return;
    if (selectedId && list.some((a) => a.id === selectedId)) {
      setAnnotations((prev) => prev.map((a) => (a.id === selectedId ? { ...a, reviewed: true } : a)));
      const rest = list.filter((a) => a.id !== selectedId);
      if (!rest.length) {
        toast.success("Último caso dudoso confirmado ✓ Todo revisado.");
        setSelectedId(null);
        return;
      }
      const next = rest.find((a) => a.keyframes[0].f > frame) || rest[0];
      setSelectedId(next.id);
      setFrame(Math.min(hiF, Math.max(loF, next.keyframes[0].f)));
      return;
    }
    const next = list.find((a) => a.keyframes[0].f > frame) || list[0];
    setSelectedId(next.id);
    setFrame(Math.min(hiF, Math.max(loF, next.keyframes[0].f)));
  };
  const frameW = vZoom >= 2 ? 1920 : 960;
  const secLbl = (n) => fmtS(n, fps);

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-5 items-start">
      {/* ============ PANEL IZQUIERDO ============ */}
      <aside className="rounded-3xl bg-[#FFF7F0] border border-[#F5E7D8] p-5">
        <p className="text-[11.5px] font-extrabold tracking-[0.14em] uppercase text-[#C2570F]">
          {mode === "manual" ? "Modo Dakota · manual asistido" : "Revisión híbrida"}
        </p>
        <p className="text-[12.5px] text-[#8A7F73] mt-1 mb-4" data-testid="ann-summary">
          {mode === "manual" && rangeAnns.length === 0
            ? "Dibuja un óvalo o recuadro: Dakota hará el resto"
            : `${rangeAnns.length} elementos${frameRange ? ` en el tramo ${frameRange.index}/${frameRange.count}` : ""} · ${dudosas > 0 ? `${dudosas} por revisar` : "todo verificado"}`}
        </p>

        {/* revisión guiada: saltar al siguiente caso dudoso */}
        {dudosas > 0 && (
          <button
            data-testid="next-doubt-btn"
            onClick={nextDoubt}
            className="w-full mb-4 h-10 rounded-xl bg-[#FFB81C]/15 border border-[#FFB81C]/50 text-[#92600A] text-[12px] font-extrabold hover:bg-[#FFB81C]/25 transition-colors inline-flex items-center justify-center gap-2"
            title="Confirma el caso actual y salta al siguiente elemento dudoso"
          >
            <Sparkles size={13} /> Revisar siguiente dudoso ({dudosas})
          </button>
        )}

        {/* propagacion inteligente */}
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#8A7F73] mb-1.5">Al corregir, aplicar a…</p>
        <div className="grid grid-cols-4 gap-1.5 mb-2">
          {[
            { id: "here", label: "Solo aquí" },
            { id: "fib", label: "N frames" },
            { id: "track", label: "Todo el track" },
            { id: "end", label: "Hasta el fin" },
          ].map((p) => (
            <button
              key={p.id}
              data-testid={`prop-mode-${p.id}`}
              onClick={() => setPropMode(p.id)}
              className={`h-9 rounded-lg text-[10.5px] font-bold leading-tight transition-all ${
                propMode === p.id ? "bg-[#F26F21] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {propMode === "fib" && (
          <div data-testid="prop-fib-panel" className="mb-2 rounded-xl bg-white border border-[#F0E2D2] p-2.5">
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <button
                data-testid="prop-dir-back"
                onClick={() => setPropDir("back")}
                className={`h-7 rounded-lg text-[11px] font-bold transition-all ${
                  propDir === "back" ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F]"
                }`}
              >
                ← Frames anteriores
              </button>
              <button
                data-testid="prop-dir-fwd"
                onClick={() => setPropDir("fwd")}
                className={`h-7 rounded-lg text-[11px] font-bold transition-all ${
                  propDir === "fwd" ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F]"
                }`}
              >
                Frames siguientes →
              </button>
            </div>
            <div className="flex gap-1 overflow-x-auto scroll-hide pb-1">
              {FIB.map((n) => (
                <Chip
                  key={n}
                  testid={`prop-fib-${n}`}
                  active={propN === n}
                  suggested={suggestion?.n === n && propN !== n}
                  onClick={() => setPropN(n)}
                  title={`${n} fotogramas = ${secLbl(n)}`}
                >
                  {n}
                </Chip>
              ))}
            </div>
            <p className="text-[10.5px] font-semibold text-[#8A7F73] mt-1.5 tabular-nums">
              {propN} fotogramas = {secLbl(propN)} {propDir === "back" ? "hacia atrás" : "hacia delante"}
            </p>
          </div>
        )}

        {suggestion && selectedId && (
          <button
            data-testid="use-suggestion-btn"
            onClick={() => { setPropMode("fib"); setPropN(suggestion.n); }}
            className="mb-4 w-full rounded-xl bg-[#FFF3E9] border border-[#F26F21]/40 px-3 py-2 text-left hover:border-[#F26F21] transition-all inline-flex items-center gap-2"
          >
            <Wand2 size={14} className="text-[#F26F21] shrink-0" />
            <span className="text-[11px] font-bold text-[#C2570F]">
              Sugerido: {suggestion.n} frames ({suggestion.tag} · {secLbl(suggestion.n)})
            </span>
          </button>
        )}

        {/* añadir + copiar/pegar */}
        <div className="grid grid-cols-2 gap-1.5 mb-1.5">
          <button data-testid="add-oval-btn" onClick={() => addManual("oval")} className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center justify-center gap-1.5">
            <Circle size={13} className="text-[#F26F21]" /> Añadir óvalo
          </button>
          <button data-testid="add-box-btn" onClick={() => addManual("box")} className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center justify-center gap-1.5">
            <Square size={13} className="text-[#F26F21]" /> Añadir recuadro
          </button>
        </div>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          <button
            data-testid="copy-btn"
            onClick={copySel}
            disabled={!selectedId}
            className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
          >
            <Copy size={13} className="text-[#F26F21]" /> Copiar <kbd className="text-[9px] text-[#8A7F73]">Ctrl+C</kbd>
          </button>
          <button
            data-testid="paste-btn"
            onClick={paste}
            disabled={!clipboard}
            className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
          >
            <ClipboardPaste size={13} className="text-[#F26F21]" /> Pegar <kbd className="text-[9px] text-[#8A7F73]">Ctrl+V</kbd>
          </button>
        </div>

        {/* elemento seleccionado: duplicar / editar / eliminar / duración */}
        {selectedId && selAnn && (
          <div data-testid="trim-controls" className="mb-4 rounded-xl bg-white border-2 border-[#F26F21]/40 p-3">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#C2570F] mb-2 truncate">
              Elemento seleccionado · <span className="capitalize">{selAnn.label}</span>
            </p>

            <div className="grid grid-cols-2 gap-1.5 mb-1.5">
              <button
                data-testid="sel-duplicate-btn"
                onClick={duplicateSel}
                className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[11.5px] font-bold text-[#241E18] hover:border-[#F26F21] transition-all inline-flex items-center justify-center gap-1.5"
              >
                <Copy size={12} className="text-[#F26F21]" /> Duplicar <kbd className="text-[9px] text-[#8A7F73]">Ctrl+D</kbd>
              </button>
              <button
                data-testid="sel-delete-btn"
                onClick={() => removeAnn(selectedId)}
                className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[11.5px] font-bold text-[#D93025] hover:border-[#D93025] hover:bg-[#FDECEA] transition-all inline-flex items-center justify-center gap-1.5"
              >
                <Trash2 size={12} /> Eliminar <kbd className="text-[9px] text-[#B4A896]">Supr</kbd>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-1.5 mb-1.5">
              <button
                data-testid="sel-shape-oval"
                onClick={() => setSelProp({ shape: "oval" }, "Forma: óvalo")}
                className={`h-8 rounded-lg text-[11px] font-bold transition-all inline-flex items-center justify-center gap-1 ${
                  selAnn.shape === "oval" ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
                }`}
              >
                <Circle size={11} /> Óvalo
              </button>
              <button
                data-testid="sel-shape-box"
                onClick={() => setSelProp({ shape: "box" }, "Forma: recuadro")}
                className={`h-8 rounded-lg text-[11px] font-bold transition-all inline-flex items-center justify-center gap-1 ${
                  selAnn.shape !== "oval" ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
                }`}
              >
                <Square size={11} /> Recuadro
              </button>
            </div>

            <div className="grid grid-cols-4 gap-1 mb-1.5">
              {[
                { id: null, label: "Global", testid: "sel-style-global" },
                { id: "oval", label: "Blur", testid: "sel-style-oval" },
                { id: "pixel", label: "Pixel", testid: "sel-style-pixel" },
                { id: "box", label: "Sólido", testid: "sel-style-box" },
              ].map((s) => (
                <button
                  key={s.testid}
                  data-testid={s.testid}
                  onClick={() => setSelProp({ style: s.id }, s.id ? `Estilo propio: ${s.label.toLowerCase()}` : "Usará el estilo global")}
                  className={`h-7 rounded-lg text-[10px] font-bold transition-all ${
                    (selAnn.style || null) === s.id ? "bg-[#F26F21] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <button
                data-testid="sel-lock-btn"
                onClick={() => setSelProp({ locked: !selAnn.locked }, selAnn.locked ? "Elemento desbloqueado" : "Elemento bloqueado")}
                className="h-8 rounded-lg bg-white border border-[#F0E2D2] text-[11px] font-bold text-[#57503F] hover:border-[#F26F21]/50 transition-all inline-flex items-center justify-center gap-1"
              >
                <Lock size={11} /> {selAnn.locked ? "Desbloquear" : "Bloquear"}
              </button>
              <button
                data-testid="sel-review-btn"
                onClick={() => setSelProp({ reviewed: !selAnn.reviewed }, selAnn.reviewed ? "Marca de revisado quitada" : "Marcado como revisado")}
                className="h-8 rounded-lg bg-white border border-[#F0E2D2] text-[11px] font-bold text-[#57503F] hover:border-[#2FA45A] hover:text-[#2FA45A] transition-all inline-flex items-center justify-center gap-1"
              >
                <CheckCheck size={11} /> {selAnn.reviewed ? "Quitar revisado" : "Revisado"}
              </button>
            </div>

            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#8A7F73] mb-1">Seguimiento Dakota · sin OCR</p>
            <div className="grid grid-cols-3 gap-1 mb-1.5">
              {[
                { id: "region", label: "Región exacta", testid: "track-mode-region" },
                { id: "plate", label: "Matrícula", testid: "track-mode-plate" },
                { id: "vehicle", label: "Vehículo", testid: "track-mode-vehicle" },
              ].map((m) => (
                <button
                  key={m.id}
                  data-testid={m.testid}
                  onClick={() => setTrackMode(m.id)}
                  title={m.id === "vehicle" ? "Sigue el vehículo completo y recalcula la posición de la matrícula" : "Sigue la región marcada"}
                  className={`h-7 rounded-lg text-[9.5px] font-bold leading-tight transition-all ${
                    trackMode === m.id ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-1 mb-1.5">
              {[
                { id: "pos", label: "Posición", testid: "track-fit-pos" },
                { id: "pos_size", label: "Posición + Tamaño", testid: "track-fit-pos-size" },
              ].map((m) => (
                <button
                  key={m.id}
                  data-testid={m.testid}
                  onClick={() => setTrackFit(m.id)}
                  title={m.id === "pos_size" ? "La máscara crece o se reduce si el objeto se acerca o se aleja (por defecto)" : "Tamaño fijo, solo se mueve"}
                  className={`h-7 rounded-lg text-[10px] font-bold transition-all ${
                    trackFit === m.id ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {trackBusy ? (
              <div className="mb-2">
                <p data-testid="track-info" className="text-[10.5px] font-bold text-[#C2570F] tabular-nums mb-1 inline-flex items-center gap-1.5">
                  <Loader2 size={11} className="animate-spin" /> {trackInfo || "Siguiendo…"}
                </p>
                <button
                  data-testid="track-stop-btn"
                  onClick={() => { cancelTrackRef.current = true; }}
                  className="w-full h-8 rounded-lg bg-[#FDECEA] border border-[#F5C6C0] text-[11px] font-extrabold text-[#A4271C] hover:bg-[#F5C6C0]/50 transition-all"
                >
                  Detener seguimiento
                </button>
              </div>
            ) : (
              <div className="mb-2 space-y-1.5">
                <button
                  data-testid="teach-dakota-btn"
                  onClick={teachDakota}
                  disabled={!selVisible}
                  title="Dakota aprende la apariencia del objeto y crea un Track ID persistente"
                  className="w-full h-9 rounded-lg bg-[#241E18] text-white text-[11.5px] font-extrabold hover:bg-[#3A2F24] transition-all inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Wand2 size={13} className="text-[#FFB81C]" /> Enseñar a Dakota
                </button>
                <button
                  data-testid="track-btn"
                  onClick={followUntilGone}
                  disabled={!selVisible}
                  className="w-full h-9 rounded-lg bg-[#F26F21] text-white text-[11.5px] font-extrabold hover:bg-[#D95B12] transition-all inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Crosshair size={13} /> Seguir hasta desaparecer
                </button>
              </div>
            )}

            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#8A7F73] mb-1">Duración</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                data-testid="trim-start-btn"
                onClick={() => trimAnn(selectedId, "start")}
                disabled={!selVisible}
                className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[11.5px] font-bold text-[#241E18] hover:border-[#2FA45A] hover:text-[#2FA45A] transition-all inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Eye size={13} /> Aparece aquí
              </button>
              <button
                data-testid="trim-end-btn"
                onClick={() => trimAnn(selectedId, "end")}
                disabled={!selVisible}
                className="h-9 rounded-lg bg-white border border-[#F0E2D2] text-[11.5px] font-bold text-[#241E18] hover:border-[#D93025] hover:text-[#D93025] transition-all inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <EyeOff size={13} /> Desaparece aquí
              </button>
            </div>
            {!selVisible && (
              <p data-testid="trim-hint" className="mt-1.5 text-[10.5px] font-semibold text-[#92600A]">
                El elemento no es visible en este fotograma: navega a un fotograma dentro de su rango.
              </p>
            )}
          </div>
        )}

        {/* estilo global */}
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#8A7F73] mb-1.5">Estilo de render</p>
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {[{ id: "box", label: "Recuadro" }, { id: "pixel", label: "Pixelado" }, { id: "oval", label: "Ovalado" }].map((s) => (
            <button
              key={s.id}
              onClick={() => setStyle(s.id)}
              className={`h-8 rounded-lg text-[11.5px] font-bold transition-all ${
                style === s.id ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* intensidad global */}
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#8A7F73] mb-1.5">Intensidad</p>
        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {[{ id: 1, label: "Suave" }, { id: 2, label: "Media" }, { id: 3, label: "Fuerte" }].map((s) => (
            <button
              key={s.id}
              data-testid={`editor-strength-${s.id}-btn`}
              onClick={() => setStrength && setStrength(s.id)}
              className={`h-8 rounded-lg text-[11.5px] font-bold transition-all ${
                (strength || 3) === s.id ? "bg-[#F26F21] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* edicion masiva */}
        {checked.size > 0 && (
          <div data-testid="bulk-panel" className="mb-3 rounded-xl bg-[#241E18] p-3">
            <p className="text-[11px] font-extrabold text-[#FFB81C] mb-2">{checked.size} {checked.size === 1 ? "seleccionado" : "seleccionados"} · edición masiva</p>
            <div className="grid grid-cols-2 gap-1.5 mb-1.5">
              <button data-testid="bulk-shape-oval" onClick={() => bulkSet((a) => ({ ...a, shape: "oval" }), "Convertidos a óvalo")} className="h-8 rounded-lg bg-white/10 text-white text-[11px] font-bold hover:bg-white/20 transition-all">→ Óvalo</button>
              <button data-testid="bulk-shape-box" onClick={() => bulkSet((a) => ({ ...a, shape: "box" }), "Convertidos a recuadro")} className="h-8 rounded-lg bg-white/10 text-white text-[11px] font-bold hover:bg-white/20 transition-all">→ Recuadro</button>
            </div>
            <div className="grid grid-cols-3 gap-1.5 mb-1.5">
              <button data-testid="bulk-style-oval" onClick={() => bulkSet((a) => ({ ...a, style: "oval" }), "Estilo: blur suave")} className="h-8 rounded-lg bg-white/10 text-white text-[10.5px] font-bold hover:bg-white/20 transition-all">Blur</button>
              <button data-testid="bulk-style-pixel" onClick={() => bulkSet((a) => ({ ...a, style: "pixel" }), "Estilo: pixelado")} className="h-8 rounded-lg bg-white/10 text-white text-[10.5px] font-bold hover:bg-white/20 transition-all">Pixelado</button>
              <button data-testid="bulk-style-box" onClick={() => bulkSet((a) => ({ ...a, style: "box" }), "Estilo: sólido")} className="h-8 rounded-lg bg-white/10 text-white text-[10.5px] font-bold hover:bg-white/20 transition-all">Sólido</button>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <button data-testid="bulk-lock" onClick={() => bulkSet((a) => ({ ...a, locked: !a.locked }), "Bloqueo alternado")} className="h-8 rounded-lg bg-white/10 text-white text-[10.5px] font-bold hover:bg-white/20 transition-all inline-flex items-center justify-center gap-1"><Lock size={11} /> Bloquear</button>
              <button data-testid="bulk-review" onClick={() => bulkSet((a) => ({ ...a, reviewed: true }), "Marcados como revisados")} className="h-8 rounded-lg bg-white/10 text-white text-[10.5px] font-bold hover:bg-white/20 transition-all inline-flex items-center justify-center gap-1"><CheckCheck size={11} /> Revisado</button>
              <button data-testid="bulk-delete" onClick={bulkDelete} className="h-8 rounded-lg bg-[#D93025] text-white text-[10.5px] font-bold hover:bg-[#B3261E] transition-all inline-flex items-center justify-center gap-1"><Trash2 size={11} /> Eliminar</button>
            </div>
          </div>
        )}

        {/* lista */}
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#8A7F73]">Elementos</p>
          <button data-testid="select-all-btn" onClick={toggleAll} className="text-[10.5px] font-bold text-[#C2570F] hover:underline">
            {checked.size === rangeAnns.length && rangeAnns.length > 0 ? "Ninguno" : "Seleccionar todos"}
          </button>
        </div>
        <div className="max-h-[260px] overflow-y-auto space-y-1.5 pr-1">
          {rangeAnns.map((a) => {
            const info = confInfo(a);
            const active = interp(a.keyframes, frame) !== null;
            const kfs = a.keyframes;
            const t0 = kfs.length ? (kfs[0].f / fps).toFixed(1) : "0";
            const t1 = kfs.length ? (kfs[kfs.length - 1].f / fps).toFixed(1) : "0";
            return (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                data-testid={`ann-row-${a.id}`}
                onClick={() => {
                  setSelectedId(a.id);
                  if (!active && a.keyframes.length) setFrame(Math.min(hiF, Math.max(loF, a.keyframes[0].f)));
                }}
                className={`w-full flex items-center gap-2 rounded-xl px-2.5 py-2 text-left cursor-pointer transition-all ${
                  selectedId === a.id ? "bg-white border-2 border-[#F26F21]" : "bg-white/60 border border-[#F0E2D2] hover:border-[#F26F21]/40"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid={`ann-check-${a.id}`}
                  checked={checked.has(a.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleCheck(a.id)}
                  className="accent-[#F26F21] w-3.5 h-3.5 shrink-0 cursor-pointer"
                />
                {a.shape === "oval" ? <Circle size={13} className="text-[#8A7F73] shrink-0" /> : <Square size={13} className="text-[#8A7F73] shrink-0" />}
                <span className="flex-1 min-w-0">
                  <span className="block text-[12px] font-bold text-[#241E18] capitalize truncate">
                    {a.locked && <Lock size={10} className="inline mr-1 text-[#8A7F73]" />}
                    {a.label}
                  </span>
                  <span className="block text-[10px] font-semibold text-[#8A7F73] tabular-nums">
                    {t0}s → {t1}s{a.style ? ` · ${a.style === "pixel" ? "pixelado" : a.style === "oval" ? "blur" : "sólido"}` : ""}
                  </span>
                </span>
                <span className={`px-1.5 py-0.5 rounded-md border text-[9.5px] font-extrabold ${info.cls}`}>{info.txt}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); removeAnn(a.id); }}
                  className="text-[#B4A896] hover:text-[#D93025] transition-colors"
                >
                  <Trash2 size={13} />
                </span>
              </div>
            );
          })}
          {annotations.length === 0 && (
            <p className="text-[12px] text-[#8A7F73] px-2 py-4 text-center">
              {mode === "manual"
                ? "Dibuja un óvalo o recuadro sobre el vídeo, selecciónalo y pulsa «Enseñar a Dakota» para que lo siga automáticamente."
                : "La IA no detectó elementos. Añade óvalos o recuadros manualmente."}
            </p>
          )}
        </div>

        {/* render */}
        <button
          data-testid="render-btn"
          onClick={startRender}
          disabled={rendering || cost > credits}
          className={`mt-5 w-full h-12 rounded-full text-[14px] font-extrabold inline-flex items-center justify-center gap-2 transition-all ${
            rendering || cost > credits
              ? "bg-[#EFE6DC] text-[#B4A896] cursor-not-allowed"
              : "bg-[#F26F21] text-white hover:bg-[#D95B12] hover:shadow-xl hover:shadow-[#F26F21]/25"
          }`}
        >
          {rendering ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {cost > credits ? "Saldo insuficiente" : `Renderizar vídeo (${cost} créditos)`}
        </button>
      </aside>

      {/* ============ ESCENARIO + NAVEGACIÓN ============ */}
      <section>
        {/* barra de zoom visual */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#8A7F73]">Zoom vídeo</span>
          <button data-testid="vzoom-out-btn" onClick={() => zoomAt(stageW / 2, baseH / 2, false)} className="w-7 h-7 rounded-lg bg-white border border-[#F0E2D2] flex items-center justify-center text-[#57503F] hover:border-[#F26F21] transition-all">
            <ZoomOut size={13} />
          </button>
          <span data-testid="vzoom-value" className="text-[12px] font-extrabold text-[#241E18] tabular-nums w-[52px] text-center">{vZoom * 100}%</span>
          <button data-testid="vzoom-in-btn" onClick={() => zoomAt(stageW / 2, baseH / 2, true)} className="w-7 h-7 rounded-lg bg-white border border-[#F0E2D2] flex items-center justify-center text-[#57503F] hover:border-[#F26F21] transition-all">
            <ZoomIn size={13} />
          </button>
          <button data-testid="vzoom-reset-btn" onClick={() => { setVZoom(1); setPan({ x: 0, y: 0 }); }} className="w-7 h-7 rounded-lg bg-white border border-[#F0E2D2] flex items-center justify-center text-[#57503F] hover:border-[#F26F21] transition-all">
            <Maximize size={12} />
          </button>
          <span className="text-[10.5px] text-[#8A7F73] font-semibold">Rueda del ratón = zoom · arrastra el lienzo para desplazarte</span>
        </div>

        {/* escenario */}
        <div
          ref={outerRef}
          data-testid="stage"
          className="relative rounded-2xl overflow-hidden bg-black select-none touch-none"
          style={baseH ? { height: baseH } : {}}
          onPointerMove={onStagePointerMove}
          onPointerUp={onStagePointerUp}
          onPointerLeave={onStagePointerUp}
        >
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Loader2 size={26} className="text-white/40 animate-spin" />
          </div>
          <div
            className="relative"
            style={{ width: `${vZoom * 100}%`, transform: `translate(${pan.x}px, ${pan.y}px)` }}
            onPointerDown={onStageBgDown}
          >
            <img
              src={`${API}/videos/${videoId}/frame/${frame}?w=${frameW}`}
              alt={`Fotograma ${frame}`}
              className={`w-full block ${vZoom > 1 ? "cursor-grab" : ""}`}
              draggable={false}
            />
            {visibleAnns.map(({ ann, rect }) => {
              const sel = selectedId === ann.id;
              const color = borderColor(ann, sel);
              return (
                <div
                  key={ann.id}
                  data-testid={`overlay-${ann.id}`}
                  onPointerDown={(e) => startDrag(e, ann, "move")}
                  className={ann.locked ? "absolute cursor-not-allowed" : "absolute cursor-move"}
                  style={{
                    left: rect.x * imgScale,
                    top: rect.y * imgScale,
                    width: rect.w * imgScale,
                    height: rect.h * imgScale,
                    border: `2px ${sel ? "solid" : "dashed"} ${color}`,
                    borderRadius: ann.shape === "oval" ? "50%" : 6,
                    backgroundColor: `${color}22`,
                    backdropFilter: sel ? "none" : "blur(2px)",
                    zIndex: sel ? 20 : 10,
                  }}
                >
                  {sel && !ann.locked && (
                    <span
                      onPointerDown={(e) => startDrag(e, ann, "resize")}
                      className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#F26F21] border-2 border-white cursor-nwse-resize"
                    />
                  )}
                </div>
              );
            })}
          </div>
          <span className="absolute top-3 left-3 z-30 px-3 py-1 rounded-full bg-black/60 text-white text-[11px] font-bold tabular-nums pointer-events-none">
            {frameRange ? `Tramo ${frameRange.index}/${frameRange.count} · ` : ""}Fotograma {frame} / {hiF} · {(frame / fps).toFixed(2)}s{vZoom > 1 ? ` · ${vZoom * 100}%` : ""}
          </span>
          {selectedId === null && vZoom === 1 && (
            <span className="absolute bottom-3 left-3 z-30 px-3 py-1.5 rounded-full bg-black/60 text-white text-[11px] font-semibold inline-flex items-center gap-1.5 pointer-events-none">
              <MousePointerClick size={12} /> Toca un elemento para moverlo o redimensionarlo
            </span>
          )}
        </div>

        {/* navegacion fibonacci */}
        <div className="mt-3 rounded-2xl bg-[#FFF7F0] border border-[#F5E7D8] p-3">
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <button data-testid="nav-back-jump" onClick={() => go(-jump)} className="h-8 px-3 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#57503F] hover:border-[#F26F21] transition-all inline-flex items-center gap-1 tabular-nums">
              <StepBack size={11} /> -{jump}
            </button>
            <button data-testid="nav-back-1" onClick={() => go(-1)} className="h-8 px-3 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#57503F] hover:border-[#F26F21] transition-all inline-flex items-center gap-1">
              <StepBack size={11} /> -1
            </button>
            <button data-testid="nav-fwd-1" onClick={() => go(1)} className="h-8 px-3 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#57503F] hover:border-[#F26F21] transition-all inline-flex items-center gap-1">
              +1 <StepForward size={11} />
            </button>
            <button data-testid="nav-fwd-jump" onClick={() => go(jump)} className="h-8 px-3 rounded-lg bg-white border border-[#F0E2D2] text-[12px] font-bold text-[#57503F] hover:border-[#F26F21] transition-all inline-flex items-center gap-1 tabular-nums">
              +{jump} <StepForward size={11} />
            </button>
          </div>
          <div className="flex gap-1 overflow-x-auto scroll-hide pb-1 justify-start sm:justify-center">
            {FIB.map((n) => (
              <Chip key={n} testid={`fib-jump-${n}`} active={jump === n} onClick={() => setJump(n)} title={`${n} fotogramas = ${secLbl(n)}`}>
                {n}
              </Chip>
            ))}
          </div>
          <p data-testid="jump-equivalence" className="text-center text-[10.5px] font-semibold text-[#8A7F73] mt-1.5 tabular-nums">
            Salto: {jump} {jump === 1 ? "fotograma" : "fotogramas"} = {secLbl(jump)} · {Math.round(fps)} FPS · Flechas ←→ = ±1 · Shift+flechas = ±{jump}
          </p>
        </div>

        {/* timeline con zoom temporal */}
        <div ref={tlRef} data-testid="timeline" className="mt-3 rounded-2xl bg-[#FFF7F0] border border-[#F5E7D8] p-3">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#8A7F73] mr-1">Zoom timeline</span>
            <button data-testid="tl-zoom-out" onClick={() => setLevel(tlLevel - 1)} disabled={tlLevel === 0} className="w-7 h-7 rounded-lg bg-white border border-[#F0E2D2] flex items-center justify-center text-[#57503F] hover:border-[#F26F21] disabled:opacity-40 transition-all">
              <ZoomOut size={13} />
            </button>
            {TL_LEVELS.map((lv, i) => (
              <button
                key={lv.id}
                data-testid={`tl-level-${lv.id}`}
                onClick={() => setLevel(i)}
                className={`h-7 px-2.5 rounded-lg text-[10.5px] font-bold transition-all ${
                  tlLevel === i ? "bg-[#241E18] text-white" : "bg-white border border-[#F0E2D2] text-[#57503F] hover:border-[#F26F21]/50"
                }`}
              >
                {lv.label}
              </button>
            ))}
            <button data-testid="tl-zoom-in" onClick={() => setLevel(tlLevel + 1)} disabled={tlLevel === TL_LEVELS.length - 1} className="w-7 h-7 rounded-lg bg-white border border-[#F0E2D2] flex items-center justify-center text-[#57503F] hover:border-[#F26F21] disabled:opacity-40 transition-all">
              <ZoomIn size={13} />
            </button>
            <span className="text-[10px] text-[#8A7F73] font-semibold ml-1">Ctrl + rueda</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="tl-prev-window"
              onClick={() => setTlStart((s) => Math.max(0, s - curSpan))}
              disabled={tlStart <= 0}
              className="w-8 h-14 rounded-lg bg-white border border-[#F0E2D2] flex items-center justify-center text-[#57503F] hover:border-[#F26F21] disabled:opacity-40 transition-all shrink-0"
            >
              <ChevronLeft size={15} />
            </button>
            <div className="flex gap-1.5 overflow-x-auto scroll-hide flex-1">
              {stripItems.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setFrame(t.f)}
                  className={`relative shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                    Math.abs(frame - t.f) < Math.max(1, (curSpan * fps) / N_THUMBS / 2) ? "border-[#F26F21]" : "border-transparent hover:border-[#F26F21]/50"
                  }`}
                >
                  <img
                    src={`${API}/videos/${videoId}/frame/${t.f}?w=160`}
                    alt={t.label}
                    loading="lazy"
                    className="w-[74px] h-[46px] object-cover block"
                    draggable={false}
                  />
                  <span className="absolute bottom-0 right-0 px-1 bg-black/60 text-white text-[9px] font-bold rounded-tl tabular-nums">{t.label}</span>
                </button>
              ))}
            </div>
            <button
              data-testid="tl-next-window"
              onClick={() => setTlStart((s) => Math.min(Math.max(0, totalSecs - curSpan), s + curSpan))}
              disabled={tlStart + curSpan >= totalSecs}
              className="w-8 h-14 rounded-lg bg-white border border-[#F0E2D2] flex items-center justify-center text-[#57503F] hover:border-[#F26F21] disabled:opacity-40 transition-all shrink-0"
            >
              <ChevronRight size={15} />
            </button>
          </div>

          {/* mini-mapa de posicion */}
          <div
            data-testid="tl-minimap"
            className="relative h-2.5 mt-2 rounded-full bg-[#F0E2D2] cursor-pointer"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const sec = ((e.clientX - r.left) / r.width) * totalSecs;
              setFrame(Math.min(hiF, Math.max(loF, Math.round((loSec + sec) * fps) + 1)));
            }}
          >
            <div
              className="absolute top-0 h-full rounded-full bg-[#F26F21]/30"
              style={{ left: `${(tlStart / totalSecs) * 100}%`, width: `${Math.min(100, (curSpan / totalSecs) * 100)}%` }}
            />
            <div
              className="absolute top-[-2px] w-[3px] h-[14px] rounded bg-[#F26F21]"
              style={{ left: `${Math.min(99.5, (((frame - loF) / fps) / totalSecs) * 100)}%` }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewEditor;
